import React, { useState } from "react";
import { Download } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa6";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";

const HERO = "https://images.pexels.com/photos/15010285/pexels-photo-15010285.jpeg?auto=compress&cs=tinysrgb&w=1200";

export const Franchise = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/franchise", form);
      toast.success(t("franchise_thanks"));
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  const waNum = (settings.whatsapp_franchise || settings.whatsapp || "").replace(/[^0-9]/g, "");
  const waLink = `https://wa.me/${waNum}?text=${encodeURIComponent("Hi Panda Tata! I'm interested in franchising.")}`;

  return (
    <div>
      <section className="relative overflow-hidden bg-brand-ink text-brand-cream grain">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-5 py-16 lg:grid-cols-2">
          <div>
            <h1 className="font-display text-5xl font-black tracking-tighter text-brand-yellow sm:text-6xl">{t("franchise_title")}</h1>
            <p className="mt-4 max-w-md text-lg text-brand-cream/80">{t("franchise_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {settings.brandbook_path ? (
                <a href={fileUrl(settings.brandbook_path)} target="_blank" rel="noopener noreferrer" data-testid="download-brandbook" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-yellow bg-brand-yellow px-6 py-3 font-bold text-brand-ink hover-brutal">
                  <Download className="h-4 w-4" /> {t("download_brandbook")}
                </a>
              ) : (
                <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border-2 border-brand-cream/30 bg-brand-cream/10 px-6 py-3 font-bold text-brand-cream/60">
                  <Download className="h-4 w-4" /> {t("download_brandbook")} <span className="text-xs font-normal">({t("coming_soon")})</span>
                </span>
              )}
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="franchise-whatsapp" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-green bg-brand-green px-6 py-3 font-bold text-brand-ink hover-brutal">
                <FaWhatsapp className="h-4 w-4" /> {t("chat_whatsapp")}
              </a>
            </div>
          </div>
          <img src={settings.home?.franchise_image ? fileUrl(settings.home.franchise_image) : HERO} alt="Franchise" className="aspect-[4/3] w-full rounded-3xl border-2 border-brand-yellow object-cover" />
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-5 py-16">
        <div className="rounded-3xl border-2 border-brand-ink bg-white p-8 shadow-brutal-lg">
          <h2 className="font-display text-3xl font-bold">{t("home_franchise_cta")}</h2>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="franchise-form">
            <div>
              <label className="text-sm font-semibold">{t("form_name")}</label>
              <input data-testid="franchise-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form_phone")}</label>
              <input data-testid="franchise-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form_email")}</label>
              <input data-testid="franchise-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form_message")}</label>
              <textarea data-testid="franchise-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <button type="submit" disabled={sending} data-testid="franchise-submit" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-3 font-bold hover-brutal disabled:opacity-60">
              {sending ? "..." : t("submit")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Franchise;
